import MUIDataTable from "mui-datatables";
import * as React from 'react'
import api from '../services/api'
import './style.css'
import {
  Alert, Badge, Box, Button, Container, Dialog, DialogContent,
  DialogTitle, IconButton, Paper, Snackbar, ThemeProvider,
  Tooltip, TextField, MenuItem, Typography,
} from "@mui/material";
import theme from "../themeDash";
import { grey, lightBlue, red, orange, lime, blueGrey, purple, teal, green } from "@mui/material/colors";
import RestoreIcon from '@mui/icons-material/Restore';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import moment from "moment";
import 'moment/locale/pt-br'
import { useAuth } from '../auth/AuthProvider'
import { useNavigate } from "react-router-dom";

function parseDate(val) {
  if (!val) return null
  const m = moment(val, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss', moment.ISO_8601], true)
  return m.isValid() ? m : null
}

function formatDate(val) {
  const m = parseDate(val)
  return m ? m.locale('pt-br').format('DD/MM HH:mm') : '-'
}

function formatDateRelative(val) {
  const m = parseDate(val)
  return m ? m.locale('pt-br').fromNow() : ''
}

function getSeverityColor(severity) {
  switch (severity) {
    case 5: return theme.palette.mode === 'dark' ? red[400] : red[300]
    case 4: return theme.palette.mode === 'dark' ? orange[300] : orange[200]
    case 3: return theme.palette.mode === 'dark' ? lime[300] : lime[200]
    case 2: return theme.palette.mode === 'dark' ? lightBlue[300] : lightBlue[200]
    case 1: return theme.palette.mode === 'dark' ? blueGrey[300] : blueGrey[200]
    default: return undefined
  }
}

export default function DismissedAlarms() {
  const auth = useAuth()
  const navigate = useNavigate()

  const [problems, setProblems] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [page, setPage] = React.useState(0)
  const [perPage, setPerPage] = React.useState(50)
  const [totalCount, setTotalCount] = React.useState(0)
  const [search, setSearch] = React.useState('')
  const [searchDebounced, setSearchDebounced] = React.useState('')
  const [sourceFilter, setSourceFilter] = React.useState('')
  const [empresaFilter, setEmpresaFilter] = React.useState('')
  const [empresas, setEmpresas] = React.useState([])
  const [snack, setSnack] = React.useState({ open: false, message: '', severity: 'success' })

  // Ack dialog
  const [ackDialogOpen, setAckDialogOpen] = React.useState(false)
  const [ackDialogAlarm, setAckDialogAlarm] = React.useState(null)

  React.useEffect(() => {
    if (!auth.hasPerm('problem.get')) {
      navigate('/')
      return
    }
    api.get('/empresa/all').then(res => {
      setEmpresas(res.data.filter(e => e.status === 1).map(e => e.cod_empresa).sort((a, b) => a - b))
    }).catch(() => {})
  }, [auth, navigate])

  React.useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 400)
    return () => clearTimeout(timer)
  }, [search])

  const fetchDismissed = React.useCallback(() => {
    setLoading(true)
    const params = { page: page + 1, per_page: perPage }
    if (searchDebounced) params.search = searchDebounced
    if (sourceFilter) params.source = sourceFilter
    if (empresaFilter) params.cod_empresa = empresaFilter

    api.get('/dashboard/problems/dismissed/', { params })
      .then(res => {
        setProblems(res.data.items || [])
        setTotalCount(res.data.total || 0)
      })
      .catch(() => {
        setSnack({ open: true, message: 'Erro ao carregar alarmes dispensados', severity: 'error' })
      })
      .finally(() => setLoading(false))
  }, [page, perPage, searchDebounced, sourceFilter, empresaFilter])

  React.useEffect(() => {
    fetchDismissed()
  }, [fetchDismissed])

  const handleRestore = (cod_empresa, event_id) => {
    if (!window.confirm('Restaurar este alarme ao painel ativo?')) return
    api.post('/webhooks/restore', { cod_empresa, event_id })
      .then(() => {
        setProblems(prev => prev.filter(p => !(p.cod_empresa === cod_empresa && p.event_id === event_id)))
        setTotalCount(prev => Math.max(0, prev - 1))
        setSnack({ open: true, message: 'Alarme restaurado com sucesso', severity: 'success' })
      })
      .catch(() => {
        setSnack({ open: true, message: 'Erro ao restaurar alarme', severity: 'error' })
      })
  }

  const openAckDialog = (problem) => {
    setAckDialogAlarm(problem)
    setAckDialogOpen(true)
  }

  const columns = [
    {
      name: "alarme",
      label: "Alarme",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const p = problems[dataIndex]
          if (!p) return null
          return (
            <Box sx={{ py: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography fontSize={11} fontWeight="bold" sx={{
                    px: 0.6, py: 0.1, borderRadius: 1, lineHeight: 1.4,
                    color: '#fff',
                    backgroundColor: (p.source || 'zabbix') === 'wazuh' ? purple[400] : teal[600],
                  }}>{(p.source || 'zabbix') === 'wazuh' ? 'W' : 'Z'}</Typography>
                  <Typography fontSize={14} fontWeight="bold">{p.cod_empresa}</Typography>
                  <Typography fontSize={14}>-</Typography>
                  <Typography fontSize={14} fontWeight="bold">{p.hostname}</Typography>
                  {p.ip && <Typography fontSize={14} sx={{ opacity: 0.85 }}>({p.ip})</Typography>}
                </Box>
                <Typography fontSize={14} noWrap>
                  {formatDate(p.clock)} ({formatDateRelative(p.clock)})
                </Typography>
              </Box>
              <Typography fontSize={14}>{p.name}</Typography>
            </Box>
          )
        }
      }
    },
    {
      name: "dismissed_info",
      label: "Dispensado",
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({ style: { width: 180 } }),
        customBodyRenderLite: (dataIndex) => {
          const p = problems[dataIndex]
          if (!p) return null
          return (
            <Box>
              <Typography fontSize={13}>{formatDate(p.resolved_at)}</Typography>
              <Typography fontSize={12} sx={{ opacity: 0.7 }}>{p.resolved_by_name || '-'}</Typography>
            </Box>
          )
        }
      }
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({ style: { width: 120, whiteSpace: 'nowrap' } }),
        customBodyRenderLite: (dataIndex) => {
          const p = problems[dataIndex]
          if (!p) return null
          const acks = (p.acks && p.acks.length) || 0
          return (
            <Box sx={{ display: 'flex' }}>
              <Tooltip title="Ver reconhecimentos">
                <IconButton
                  sx={{ backgroundColor: acks > 0 ? grey[900] : '', color: acks > 0 ? 'white' : grey[900] }}
                  onClick={() => openAckDialog(p)}
                  size="small"
                >
                  <Badge badgeContent={acks} color="success">
                    {acks > 0
                      ? <AssignmentTurnedInIcon sx={{ color: "white" }} />
                      : <AssignmentLateIcon sx={{ color: grey[900] }} />
                    }
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title="Restaurar alarme ao painel ativo">
                <IconButton
                  size="small"
                  onClick={() => handleRestore(p.cod_empresa, p.event_id)}
                  disabled={!auth.hasPerm('problem.edit')}
                  sx={{ color: green[600], ml: 0.5 }}
                >
                  <RestoreIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )
        },
      },
    },
  ]

  const options = React.useMemo(() => ({
    serverSide: true,
    count: totalCount,
    page: page,
    rowsPerPage: perPage,
    rowsPerPageOptions: [25, 50, 100],
    onChangePage: (p) => setPage(p),
    onChangeRowsPerPage: (rpp) => { setPerPage(rpp); setPage(0) },
    selectableRows: 'none',
    filter: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    responsive: 'standard',
    rowHover: false,
    setRowProps: (row, dataIndex) => {
      const p = problems[dataIndex]
      const bgColor = p ? getSeverityColor(p.severity) : undefined
      return {
        style: {
          backgroundColor: bgColor,
          transition: 'all 0.3s ease-in-out',
        }
      }
    },
    textLabels: {
      body: { noMatch: loading ? 'Carregando...' : 'Nenhum alarme dispensado encontrado' },
      pagination: { rowsPerPage: 'Linhas por pagina' },
    },
  }), [totalCount, page, perPage, loading, problems])

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            label="Buscar (nome, host, IP)"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
            sx={{ width: 300 }}
          />
          <TextField
            label="Empresa"
            variant="outlined"
            size="small"
            select
            value={empresaFilter}
            onChange={(e) => { setEmpresaFilter(e.target.value); setPage(0) }}
            sx={{ width: 150 }}
          >
            <MenuItem value="">Todas</MenuItem>
            {empresas.map(cod => (
              <MenuItem key={cod} value={cod}>{cod}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Source"
            variant="outlined"
            size="small"
            select
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); setPage(0) }}
            sx={{ width: 150 }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="zabbix">Zabbix</MenuItem>
            <MenuItem value="wazuh">Wazuh</MenuItem>
          </TextField>
          <Button variant="outlined" size="small" onClick={fetchDismissed} sx={{ ml: 'auto' }}>
            Atualizar
          </Button>
        </Box>
        <MUIDataTable
          title={`Alarmes Dispensados (${totalCount})`}
          data={problems}
          columns={columns}
          options={options}
        />

        {/* Dialog de Acks */}
        <Dialog open={ackDialogOpen} onClose={() => setAckDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ pb: 1 }}>
            {ackDialogAlarm && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {ackDialogAlarm.cod_empresa} - {ackDialogAlarm.hostname}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ackDialogAlarm.name}
                </Typography>
              </Box>
            )}
          </DialogTitle>
          <DialogContent dividers>
            {(!ackDialogAlarm?.acks || ackDialogAlarm.acks.length === 0) ? (
              <Typography sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                Nenhum reconhecimento encontrado
              </Typography>
            ) : (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {ackDialogAlarm.acks.map((ack, i) => (
                  <Alert key={i} sx={{ mb: 0.5, backgroundColor: grey[900], py: 0 }} severity="info" icon={false}>
                    <Typography variant="caption" color="text.secondary">
                      {ack.clock ? `${moment(ack.clock).locale('pt-br').format('DD/MM/YYYY HH:mm')} - ${moment(ack.clock).locale('pt-br').fromNow()}` : ''}
                    </Typography>
                    <Typography variant="body2">{ack.description}</Typography>
                  </Alert>
                ))}
              </Box>
            )}
          </DialogContent>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snack.severity} onClose={() => setSnack(prev => ({ ...prev, open: false }))}>
            {snack.message}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  )
}
